/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { listVehicles } from '../infra/vehiclesRepo';
import { listDueServiceReminders } from '../infra/serviceRemindersRepo';
import { vehicleDisplay } from '../domain/types';
import { buildDueReminderRows } from '../domain/dueReminders';
import type { DueReminderRow } from '../domain/dueReminders';
import type { VehicleListItem } from '../domain/types';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';

type VehiclesNavProp = NativeStackNavigationProp<{
  VehicleForm: { vehicleId?: string; vehicle?: VehicleListItem };
  ServiceReminders: { vehicleId?: string; vehicleLabel?: string };
}>;

export function VehiclesScreen() {
  const navigation = useNavigation<VehiclesNavProp>();
  const { entitlements } = usePlanEntitlements();
  const organizationId = entitlements?.organization_id ?? null;
  const branchId = useSessionStore((s) => s.currentBranchId);
  const workOrdersGate = useCapabilityGate('work_orders', {
    title: 'Vehículos no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [dueRows, setDueRows] = useState<DueReminderRow[]>([]);
  const [dueError, setDueError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listVehicles(organizationId, { search: search.trim() || null });
      setVehicles(data);
      try {
        const due = await listDueServiceReminders();
        setDueRows(buildDueReminderRows(due, data));
        setDueError(null);
      } catch {
        setDueRows([]);
        setDueError('No se pudieron cargar los services vencidos');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los vehículos');
    } finally {
      setLoading(false);
    }
  }, [organizationId, search]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const goToCreate = () => {
    navigation.navigate('VehicleForm', {});
  };

  const goToEdit = (v: VehicleListItem) => {
    navigation.navigate('VehicleForm', { vehicleId: v.id, vehicle: v });
  };

  const goToReminders = (row: DueReminderRow) => {
    navigation.navigate('ServiceReminders', {
      vehicleId: row.vehicleId,
      vehicleLabel: row.vehicleLabel,
    });
  };

  const renderItem = ({ item }: { item: VehicleListItem }) => {
    const display = vehicleDisplay({
      brand: item.brand,
      model: item.model,
      year: item.year,
      plate: item.plate,
    });
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => goToEdit(item)}
        accessibilityRole="button"
        accessibilityLabel={`Editar vehículo ${display}`}
      >
        <Text style={styles.cardTitle}>{display}</Text>
        <View style={styles.cardBody}>
          {item.mileage != null ? (
            <Text style={styles.cardMeta}>{item.mileage.toLocaleString('es-AR')} km</Text>
          ) : null}
          {item.color ? <Text style={styles.cardMeta}>{item.color}</Text> : null}
          {!item.isActive ? <Text style={styles.cardInactive}>Inactivo</Text> : null}
        </View>
      </TouchableOpacity>
    );
  };

  if (!workOrdersGate.allowed) {
    return <ShellContentFrame>{workOrdersGate.blocked}</ShellContentFrame>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.searchRow}>
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder="Buscar por patente, marca o modelo"
          placeholderTextColor={colors.textSecondary}
          accessibilityLabel="Buscar vehículos"
          returnKeyType="search"
          onSubmitEditing={() => void load()}
        />
        <TouchableOpacity
          style={styles.searchBtn}
          onPress={() => void load()}
          accessibilityRole="button"
          accessibilityLabel="Buscar"
        >
          <Text style={styles.searchBtnLabel}>Buscar</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        style={styles.addBtn}
        onPress={goToCreate}
        accessibilityRole="button"
        accessibilityLabel="Nuevo vehículo"
      >
        <Text style={styles.addBtnLabel}>+ Nuevo vehículo</Text>
      </TouchableOpacity>

      {dueError ? (
        <Text style={styles.dueError} accessibilityRole="alert">
          {dueError}
        </Text>
      ) : null}
      {dueRows.length > 0 ? (
        <View style={styles.dueSection} accessibilityLabel="Services vencidos">
          <Text style={styles.dueTitle}>Services vencidos</Text>
          {dueRows.map((row) => (
            <TouchableOpacity
              key={row.id}
              style={styles.dueCard}
              onPress={() => goToReminders(row)}
              accessibilityRole="button"
              accessibilityLabel={`Ver recordatorios de ${row.vehicleLabel}`}
            >
              <View style={styles.dueCardBody}>
                <Text style={styles.dueCardTitle}>{row.label}</Text>
                <Text style={styles.dueCardMeta}>{row.vehicleLabel}</Text>
              </View>
              <Text style={styles.dueBadge}>{row.reasonLabel}</Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}

      {loading ? (
        <ActivityIndicator color={colors.accent} style={styles.loader} />
      ) : error ? (
        <Text style={styles.errorText}>{error}</Text>
      ) : vehicles.length === 0 ? (
        <Text style={styles.emptyText}>
          {search.trim() ? 'No se encontraron vehículos' : 'No hay vehículos cargados'}
        </Text>
      ) : (
        <FlatList
          data={vehicles}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          renderItem={renderItem}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    padding: spacing.md,
  },
  searchRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  searchInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: colors.textPrimary,
  },
  searchBtn: {
    backgroundColor: colors.accent,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    justifyContent: 'center',
  },
  searchBtnLabel: { ...typography.body, color: '#FFFFFF', fontWeight: '700' },
  addBtn: {
    backgroundColor: colors.accent,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignSelf: 'flex-start',
    marginBottom: spacing.md,
  },
  addBtnLabel: { ...typography.body, color: '#FFFFFF', fontWeight: '700' },
  loader: { marginTop: spacing.xl },
  errorText: { ...typography.body, color: colors.danger, marginTop: spacing.lg, textAlign: 'center' },
  emptyText: { ...typography.body, color: colors.textSecondary, marginTop: spacing.lg, textAlign: 'center' },
  listContent: { gap: spacing.sm },
  card: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
  },
  cardTitle: { ...typography.title3, color: colors.textPrimary, fontWeight: '700', marginBottom: spacing.xs },
  cardBody: { flexDirection: 'row', gap: spacing.md, flexWrap: 'wrap' },
  cardMeta: { ...typography.caption, color: colors.textSecondary },
  cardInactive: { ...typography.caption, color: colors.warning, fontWeight: '600' },
  dueSection: { marginBottom: spacing.md, gap: spacing.sm },
  dueTitle: { ...typography.title3, color: colors.textPrimary, fontWeight: '700' },
  dueError: { ...typography.caption, color: colors.danger, marginBottom: spacing.sm },
  dueCard: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.warning,
    padding: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dueCardBody: { flex: 1 },
  dueCardTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '600' },
  dueCardMeta: { ...typography.caption, color: colors.textSecondary, marginTop: 2 },
  dueBadge: {
    ...typography.caption,
    color: colors.warning,
    fontWeight: '700',
  },
});
